/**
 * Free-text guard, per docs/ARCHITECTURE.md §9.
 *
 * Free-text fields: nickname, list title, source_hint, vocabulary, and
 * class name (PLAN.md M2). Every one of them goes through here.
 * The point is not prudishness — it is that an unfiltered text field visible
 * to a class is a messaging channel, and this app deliberately has none.
 *
 * The reason is returned for the audit log only. Never tell the user which
 * word tripped the filter, or the blocklist becomes a guessing game.
 */

export type GuardField = "display_name" | "list_title" | "source_hint" | "term" | "class_name";

export type GuardReason =
  | "empty"
  | "too_long"
  | "contact_pattern"
  | "disallowed_character"
  | "blocked_word";

export interface GuardOk {
  ok: true;
  value: string;
}
export interface GuardFail {
  ok: false;
  reason: GuardReason;
}
export type GuardResult = GuardOk | GuardFail;

export const FIELD_MAX_LENGTH: Record<GuardField, number> = {
  display_name: 40,
  list_title: 60,
  source_hint: 60,
  term: 80,
  class_name: 40,
};

/**
 * Every dash a keyboard, an autocorrect or a paste can produce: U+2010 to
 * U+2015 and the minus sign. A child typing "Unit 4 - Tiere" on an iPad gets
 * an em dash back from autocorrect and cannot see the difference, so refusing
 * it only tells them their words were wrong (issue #1). None of these is a way
 * to write a URL, an address or a phone number, which is what the guard is
 * for. The phone patterns below accept the very same set as separators: a
 * number written 079—123—45—67 must not slip past just because we widened
 * what a title may contain.
 */
const DASHES = "\\-\\u2010-\\u2015\\u2212";

/**
 * Latin letters incl. accents, digits, and the punctuation a vocabulary list
 * legitimately needs. Deliberately no emoji and no @ anywhere. A slash only in
 * a word, see SLASH_FORMS.
 */
const ALLOWED_CHARACTERS = new RegExp(
  `^[\\p{Script=Latin}\\p{Mark}0-9 .,;:!?'\\u2019()\\u005b\\u005d{}${DASHES}+&=|*"\\u00b0]*$`,
  "u",
);

/**
 * Books write both forms of a word with a slash: «cher / chère», «der Bäcker /
 * die Bäckerin». Words only (issue #88): every slash sits between two
 * non-empty parts and is stored as « / », so nothing that reaches a child
 * reads as a path or a link. URLs, domains and phone numbers are caught by
 * CONTACT_PATTERNS before this runs, on the text as typed.
 */
const SLASH_PART = String.raw`[^/\s](?:[^/]*[^/\s])?`;
const SLASH_FORMS = new RegExp(`^${SLASH_PART}(?: / ${SLASH_PART})*$`);

function spaceSlashes(value: string): string {
  return value.replace(/\s*\/\s*/g, " / ").trim();
}

/**
 * The phone patterns allow one separator between digits. Spaces around a
 * slash or a dash -- «079 / 123 / 45 / 67», «079 - 123 - 45 - 67» -- would
 * make it three, so contact patterns also run on the text with those spaces
 * removed (review of #88).
 */
const SPACED_SEPARATOR = new RegExp(`\\s*([/${DASHES}])\\s*`, "g");

/**
 * With a slash, a dotted word is a short link («bit.ly/abc», «youtu.be/x»)
 * whatever its ending -- the domain pattern only knows a few. A vocabulary
 * form with a slash has no dot inside a word.
 */
const DOTTED_WORD = /[\p{L}\p{N}]\.[\p{L}\p{N}]/u;

/** Zero-width, bidi overrides, and other invisible trickery. */
const INVISIBLE = /[\u200b-\u200f\u202a-\u202e\u2060-\u2064\u206a-\u206f\ufeff\u00ad]/;

const CONTACT_PATTERNS: RegExp[] = [
  /https?:\/\//i,
  /www\./i,
  /\b[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}\b/i, // e-mail
  /@[a-z0-9._]{2,}/i, // @handle
  /\b(?:[a-z0-9-]+\.)+(?:ch|de|com|net|org|io|app|li|at|fr|uk|eu|tv|me)\b/i, // bare domain
  new RegExp(`(?:\\+41|0041|\\b0)\\s?(?:\\d[\\s./${DASHES}]?){8,}`), // Swiss phone number
  new RegExp(`\\b\\d[\\s./${DASHES}]?(?:\\d[\\s./${DASHES}]?){9,}\\b`), // any long digit run
  /\b(?:insta(?:gram)?|snap(?:chat)?|tiktok|whats\s?app|telegram|discord|signal|youtube|yt)\b/i,
];

/**
 * Base blocklist, DE/FR/EN. Kept short on purpose: the database `blocklist`
 * table is the operator-editable layer, this is only the floor so that a
 * fresh install is never wide open. Matched on word boundaries against the
 * de-accented, lowercased text.
 */
export const BASE_BLOCKLIST: readonly string[] = [
  // Insults / slurs (abbreviated set; extend via the blocklist table)
  "arschloch", "wichser", "hurensohn", "missgeburt", "spast", "spasti",
  "fotze", "schlampe", "nutte", "neger", "judensau",
  "connard", "salope", "putain", "encule", "pute", "negre",
  "asshole", "bitch", "whore", "faggot", "nigger", "retard", "cunt",
  // Sexual terms
  "ficken", "fick", "porno", "penis", "vagina", "titten", "schwanz lutschen",
  "sexe", "bite", "porn", "dick pic", "blowjob", "sex",
  // Self-harm / violence, flagged because a class-visible list is the wrong
  // place for it — an adult should see this, not a filter alone.
  "selbstmord", "suizid", "umbringen", "kill yourself", "kys",
];

const blockRegexCache = new Map<string, RegExp>();

function blockRegex(pattern: string, isRegex: boolean): RegExp {
  const key = `${isRegex ? "r" : "w"}:${pattern}`;
  const cached = blockRegexCache.get(key);
  if (cached) return cached;
  const source = isRegex
    ? pattern
    : `(?<![\\p{L}])${pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![\\p{L}])`;
  const compiled = new RegExp(source, "iu");
  blockRegexCache.set(key, compiled);
  return compiled;
}

export interface BlocklistEntry {
  pattern: string;
  isRegex: boolean;
}

function foldForMatching(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    // Collapse letter repetition so "fiiiick" is caught alongside "fick".
    .replace(/(.)\1{2,}/g, "$1")
    // Common character substitutions used to slip past filters.
    .replace(/0/g, "o")
    .replace(/1/g, "i")
    .replace(/3/g, "e")
    .replace(/4/g, "a")
    .replace(/5/g, "s")
    .replace(/\$/g, "s");
}

/**
 * Validate and clean one free-text value.
 *
 * @param extra additional blocklist entries loaded from the database.
 */
export function guardText(
  raw: string,
  field: GuardField,
  extra: readonly BlocklistEntry[] = [],
): GuardResult {
  if (typeof raw !== "string") return { ok: false, reason: "empty" };

  if (INVISIBLE.test(raw)) return { ok: false, reason: "disallowed_character" };

  const typed = raw.normalize("NFC").replace(/\s+/g, " ").trim();
  if (typed.length === 0) return { ok: false, reason: "empty" };

  const compact = typed.replace(SPACED_SEPARATOR, "$1");
  for (const pattern of CONTACT_PATTERNS) {
    if (pattern.test(typed) || pattern.test(compact)) return { ok: false, reason: "contact_pattern" };
  }
  if (typed.includes("/") && DOTTED_WORD.test(typed)) return { ok: false, reason: "contact_pattern" };

  let value = typed;
  let unslashed = typed;
  if (field === "term" && typed.includes("/")) {
    value = spaceSlashes(typed);
    if (!SLASH_FORMS.test(value)) return { ok: false, reason: "disallowed_character" };
    unslashed = value.replaceAll(" / ", " ");
  }
  if (value.length > FIELD_MAX_LENGTH[field]) return { ok: false, reason: "too_long" };
  if (!ALLOWED_CHARACTERS.test(unslashed)) return { ok: false, reason: "disallowed_character" };

  const folded = foldForMatching(value);
  for (const word of BASE_BLOCKLIST) {
    if (blockRegex(word, false).test(folded)) return { ok: false, reason: "blocked_word" };
  }
  for (const entry of extra) {
    if (blockRegex(entry.pattern, entry.isRegex).test(folded)) {
      return { ok: false, reason: "blocked_word" };
    }
  }

  return { ok: true, value };
}

/** The single message the interface shows. Never names the offending word. */
export const GUARD_MESSAGE_DE =
  "Das können wir so nicht speichern. Nimm bitte andere Wörter — " +
  "und schreib hier nichts über dich selbst.";
